import React, { Fragment, useState } from 'react';
import * as _ from 'lodash';
import { Transition } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/outline';
import { XIcon } from '@heroicons/react/solid';

export interface INotifierContext {
  showMessage: Function;
}

const NotifierContext = React.createContext<INotifierContext>({
  showMessage: () => {},
});

const NotifierProvider: React.FC = ({ children }) => {
  const [title, setTitle] = useState<string>('');
  const [message, setMessage] = useState<string | null>(null);
  const [show, setShow] = useState<boolean>(false);
  const [variant, setVariant] = useState<string>('info');
  const [action, setAction] = useState<string>('');
  const [handleAction, setHandleAction] = useState<Function | null>(null);

  const [timeoutRef, setTimeoutRef] = useState<NodeJS.Timeout | null>(null);

  const showMessage = (
    title: string,
    message: string,
    variant: string,
    action: string,
    handleAction: Function
  ) => {
    setTitle(title);
    setMessage(message);
    setVariant(variant);
    setAction(action);
    setHandleAction(handleAction);
    setShow(true);

    if (handleAction == null) {
      if (timeoutRef) clearTimeout(timeoutRef);
      setTimeoutRef(
        setTimeout(() => {
          setShow(false);
        }, 4000)
      );
    }
  };

  const handleActionClick = () => {
    handleClose();
    if (handleAction) {
      handleAction();
    }
  };

  const handleClose = () => {
    setShow(false);
    setHandleAction(null);
  };

  return (
    <>
      <NotifierContext.Provider value={{ showMessage }}>
        {children}
      </NotifierContext.Provider>
      {/* Global notification live region, render this permanently at the end of the document */}
      <div
        aria-live="assertive"
        className="absolute inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start"
      >
        {/* <div className="absolute inset-0 flex items-end justify-center px-4 py-2 pointer-events-none sm:p-2 sm:items-start sm:justify-end"></div> */}

        <div className="w-full flex flex-col items-center space-y-4 sm:items-end">
          {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
          <Transition
            show={show}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            className="max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden"
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <CheckCircleIcon
                    className="h-6 w-6 text-green-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className="text-sm font-medium text-gray-900">
                    {_.get(title, '', 'Successfully saved!')}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">{message}</p>
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                    onClick={() => {
                      setShow(false);
                    }}
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  );
};

export { NotifierContext, NotifierProvider };
